//@import dependencies
import React, {CSSProperties, useCallback, useEffect, useState} from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import ReactTooltip from 'react-tooltip'
//@end

//@import components
import PaginationComponent from 'app_components/commons/pagination'
import EmptyStateComponent from 'app_components/commons/emptyState'
import IconsComponent from 'app_components/commons/icons'
//@end

// @import hooks
import {useColors} from 'app_hooks/useColors'
// @end

// @INFO Import types
import { IPagination } from 'app_types/pagination/paginationTypes'
import { IDataTableHeaderColId, IDataTableHeaderColStyle, TypeFormatsDataTable, TypeTextAlignDataTable } from '../containers/dataTableCommon';
// @end

// @INFO Import services
// @end

// @INFO Import Styles
import './../dataTableCommon.scss'
import moment from 'moment'
import TooltipComponent from '../../TooltipComponent/TooltipComponent';

// @ADD Component types
interface IDataTableCommonLayoutProps {
  data: any[] | boolean
  keys: string[][]
  tooltipKeys?: string[]
  detailsKey?: any[]
  headerKeys?: string[] | React.ReactNode[]
  selectedRow?: null | string
  formats?: TypeFormatsDataTable[];
  sortKeys?: any | null
  pagination?: IPagination
  module?: string
  sort: any
  sortDirection: any
  hideTooltip?: boolean
  textAlign?: TypeTextAlignDataTable;
  rowButtons?: (row: any) => React.ReactNode
  firstColRowButtons?: ((row: any) => React.ReactNode)
  sortBy: (sortKey: any, data: any[]) => void
  headerButtons?: () => React.ReactNode
  onPageChange?: (page: number) => void
  onClickRow?: any
  onSearch?: any
  onAddRow?: any
  onEditRow?: any
  onViewerRow?: any
  onRemoveRow?: any
  onUpload?: any
  onDownload?: any
  onModal?: () => void
  getValue: (target: string, object: any, options?: any) => any
  onBtnAdd?: any
  onBtn?: boolean
  titleBtn?: string
  onBtnComponentAdd?: any
  emptyMessage?: any
  searchFormInputs?: any
  actionsColTitle?: string
  headerStyles?: IDataTableHeaderColStyle[]
  headerIds?: IDataTableHeaderColId[]
}

const DataTableCommonLayout: React.FC<IDataTableCommonLayoutProps> = (props) => {

  const [colors] = useColors()

  const { t } = useTranslation()
  const searchForm = useForm()
  const [dataMap, setDataMap] = useState<any>([])

  useEffect(() => {
    setDataMap(props.data)
  }, [props.data])

  const handleSort = (sortKey: any) => {
    props.sortBy(sortKey, dataMap)
  }

  const getHeaderColStyle = useCallback((key: string): CSSProperties => {
    if (props.headerStyles?.length && !!key) {
      const elementStyle = props.headerStyles.find((hs) => hs.key === key)
      if (elementStyle) {
        return elementStyle.style
      }
      return {}
    } else {
      return {}
    }
  }, [props.headerStyles])

  const getHeaderColId = useCallback((index: number): string | undefined => {
    return props.headerIds?.find((hid) => hid.index === index)?.id
  }, [props.headerIds])

  return (
    <div className='data-table-component'>
      <div className='table-toolbar'>
        {props.onSearch && (
          <form className='btn-group mr-3' onSubmit={searchForm.handleSubmit(props.onSearch)}>
            <input
              className='form-control search-input'
              type='text'
              name='search'
              id='search'
              placeholder={t('search')}
              ref={searchForm.register()}
              defaultValue={props?.searchFormInputs?.search}
            />
            <button type='submit' className='btn btn-dark px-2'>
              <IconsComponent svg='Search' width='24px' height='24px' color='white' />
            </button>
          </form>
        )}

        {props.onAddRow && (
          <>
            <button className='btn btn-link btn-sm' data-tip={t('components.tooltips.add')} data-for='onAdd' onClick={() => props.onAddRow(true)}>
              <IconsComponent svg='Add'  width='24px' height='24px' />
            </button>
            <ReactTooltip className='customTheme' id='onAdd' effect='solid' />
          </>
        )}
        {props.onBtnComponentAdd && (
          <>
            <button className='button-show-create-dt' onClick={() => props.onBtnAdd()}>
              {props.onBtn ? t('buttons.cancel') : props.titleBtn}
            </button>
          </>
        )}

        {props.onUpload && (
          <button className='btn btn-link btn-sm' data-tip={t('components.tooltips.upload')} onClick={() => props.onUpload()}>
            <IconsComponent svg='Upload' width='24px' height='24px' color={colors.primary}/>
          </button>
        )}

        {props.onDownload && (
          <button className='btn btn-link btn-sm' data-tip={t('components.tooltips.download')} onClick={() => props.onDownload()}>
            <IconsComponent svg='Download' width='24px' height='24px' />
          </button>
        )}

        {props?.headerButtons && props?.headerButtons()}

        {/* {props.onModal && <CreateMassiveUserModule onModal={props.onModal} />} */}
      </div>

      {props.onBtn && props.onBtnComponentAdd}

      {!props.data && (
        <EmptyStateComponent />
      )}

      {props.data && (
        <div className='table-responsive'>
          <table className={`table`}>
            {props?.headerKeys && props?.headerKeys?.length > 0 && (
              <thead className={`${props.textAlign ? `data-table-align-${props.textAlign}`: ''}`}>
                <tr>
                  {props.headerKeys?.map((header, idx: number) => {
                    if (typeof header === 'string') {
                      return(
                        <React.Fragment key={idx}>
                          {header.toLocaleLowerCase().indexOf('avatar') > -1 && <th key={idx}></th>}
                          {header.toLocaleLowerCase().indexOf('avatar') === -1 && (
                            <th id={getHeaderColId(idx)} scope='col' key={idx}>
                              {t(header)}{' '}
                              {props.sortKeys && Array.isArray(props.sortKeys) && props.sortKeys.length > 0 && props.sortKeys.findIndex((element: any) => element.key === header) >= 0 && (
                                <button
                                  className='btn btn-link btn-sm'
                                  onClick={() => handleSort(props.sortKeys[props.sortKeys?.findIndex((element: any) => element.key === header)])}
                                >
                                  <IconsComponent
                                    svg={`${props.sort && props.sort.key === header && props.sortDirection === 'upward' ? 'ChevronUp' : 'ChevronDown'}`}
                                    color='#fd6720'
                                    width='14px'
                                    height='14px'
                                  />
                                </button>
                              )}
                            </th>
                          )}
                        </React.Fragment>
                      )
                    } else {
                      return (
                        <th scope='col' key={idx}>
                          {header}
                        </th>
                      )
                    }
                  })}
                  {(
                    (props?.rowButtons) ||
                    (props.detailsKey && props.detailsKey?.length > 0) ||
                    (typeof props?.onEditRow === 'function') ||
                    (typeof props?.onViewerRow === 'function') ||
                    (typeof props?.onRemoveRow === 'function')
                  ) && (
                    <th style={getHeaderColStyle('actions')}>{props.actionsColTitle ? props.actionsColTitle : ''}</th>
                  )}
                </tr>
              </thead>
            )}

            {dataMap instanceof Array &&
              dataMap.map((row: any) => (
                <tbody className={`${props.selectedRow === row?._id ? 'selected' : ''} ${props.textAlign ? `data-table-align-${props.textAlign}`: ''}`} key={row?._id}>
                  {/* Row */}
                  <tr className='t-row'>
                  {
                      (
                        props?.firstColRowButtons
                      ) &&
                      (
                        <td className='td-actions'>
                          <ReactTooltip className='customTheme' effect='solid' />
                          {props?.firstColRowButtons && props?.firstColRowButtons(row)}
                        </td>
                      )
                    }
                    {props.keys.map((key: string[], idxKey: number) => (
                      <td key={idxKey}>
                        {key.map((k: string, i: number) => (
                          <TooltipComponent
                            text={props.getValue(k, row)}
                            disabled={!props.tooltipKeys?.includes(k)}
                          >
                            <span key={i} className={key.indexOf('avatar') === -1 ? `text-detail ${key}` : ''}>
                              {props?.formats?.length && props.formats[idxKey] === 'date' ?
                                moment(new Date(props.getValue(k, row))).format('YYYY/MM/DD')
                              :
                                props.getValue(k, row)
                              }
                            </span>
                          </TooltipComponent>
                        ))}
                      </td>
                    ))}
                    {
                      (
                        (props?.rowButtons) ||
                        (props.detailsKey && props.detailsKey?.length > 0) ||
                        (typeof props?.onEditRow === 'function') ||
                        (typeof props?.onViewerRow === 'function') ||
                        (typeof props?.onRemoveRow === 'function')
                      ) &&
                      (
                        <td className='td-actions'>
                          {!props.hideTooltip ? <ReactTooltip className='customTheme' effect='solid' /> :null}
                          {props?.rowButtons && props?.rowButtons(row)}

                          {props.detailsKey && props.detailsKey?.length > 0 && typeof props?.onClickRow === 'function' && (
                            <button className='btn btn-link btn-sm' onClick={() => props.onClickRow(row)} data-tip={t('components.tooltips.see')}>
                              {row && props.selectedRow === row._id && <IconsComponent svg='Check' width='24px' height='24px' />}
                              {row && props.selectedRow !== row._id && <IconsComponent svg='ChevronDown' width='24px' height='24px' />}
                            </button>
                          )}

                          {typeof props?.onEditRow === 'function' && (
                            <button className='btn btn-link btn-sm' onClick={() => props?.onEditRow(true, row)} data-tip={t('components.tooltips.edit')}>
                              <IconsComponent svg='Edit' width='24px' height='24px' />
                            </button>
                          )}

                          {typeof props?.onViewerRow === 'function' && (
                            <button className='btn btn-link btn-sm' onClick={() => props?.onViewerRow(true, row)} data-tip={t('components.tooltips.view')}>
                              <IconsComponent svg='Eye' width='24px' height='24px' />
                            </button>
                          )}
                          {typeof props?.onRemoveRow === 'function' && (
                            <>
                              <button
                                className='btn btn-link btn-sm'
                                onClick={() => props?.onRemoveRow(row?._id)}
                                data-tip={t('components.tooltips.delete')}
                              >
                                <IconsComponent svg='Delete' width='24px' height='24px' />
                              </button>
                            </>
                          )}
                        </td>
                      )
                    }
                  </tr>

                  {/* Details */}
                  {row && row.details && props.detailsKey && (
                    <tr className='details'>
                      <td colSpan={props.keys.length + 1}>
                        <div className='col-md-12'>
                          <div className='row'>
                            {props.detailsKey.map((detailKey: any, i: number) => (
                              <div className='col-md-3 mb-3' key={i}>
                                <h6 className='text-uppercase'>{t(detailKey.name)}</h6>
                                <div className='text-detail'>{props.getValue(detailKey.key, row.details)} </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              ))}
          </table>
        </div>
      )}

      {props.data instanceof Array && props.data.length === 0 && (
        <EmptyStateComponent
          showAddMessage={props?.emptyMessage?.showAddMessage ? props?.emptyMessage?.showAddMessage : false}
          customMessage={props?.emptyMessage?.message ? props?.emptyMessage?.message : null}
        />
      )}

      {props.data instanceof Array && props.data.length > 0 && props.pagination && (
        <PaginationComponent data={props.pagination} onChangePage={props.onPageChange} />
      )}
    </div>
  )
}

export default DataTableCommonLayout
